import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { addDagen, agileUren, fmtDag, fmtUren, geplandeUren, isoWeek, maandagVan, mag, verschilDagen, werkpakketVoortgang } from '@ep/core';
import { api } from '../lib/api';
import { PROJECT_STATUS, useData, useIk, useTaakOpenen } from '../lib/data';
import { Icoon, type IcoonNaam } from '../ui/Icoon';
import { Laden } from '../ui/ui';

const DAGNAAM = ['ma', 'di', 'wo', 'do', 'vr'];

function groet() {
  const u = new Date().getHours();
  return u < 12 ? 'Goedemorgen' : u < 18 ? 'Goedemiddag' : 'Goedenavond';
}

/** SPEC §14.2: startscherm per rol. */
export function Start() {
  const s = useIk().data!;
  const nav = useNavigate();
  const taak = useTaakOpenen();
  const { data: d, kal } = useData();
  const beheerder = mag(s.rol, 'beheer');
  const planner = mag(s.rol, 'rooster');
  const urenAan = s.licentie?.modules.includes('UREN') ?? true;
  const koppel = useQuery({
    queryKey: ['agenda-koppelingen'],
    queryFn: () => api.get<{ organisatie: boolean; koppelingen: { engineerId: string; status: string }[] }>('/api/beheer/agenda-koppelingen'),
    enabled: beheerder,
  });

  const r = useMemo(() => {
    if (!d || !kal) return null;
    const vd = d.vandaag;
    const tot30 = addDagen(vd, 30);
    const actief = d.projecten.filter((p) => p.status !== 'AFGEROND');
    const eigen = actief.filter((p) => (p.pmEmail && p.pmEmail.toLowerCase() === s.email.toLowerCase()) || (p.pmNaam && p.pmNaam === s.naam));
    const pmModus = s.rol === 'PROJECTMANAGER';
    const projecten = pmModus && eigen.length ? eigen : actief;
    const ids = new Set(projecten.map((p) => p.id));
    const takenP = d.taken.filter((t) => (pmModus ? t.projectId && ids.has(t.projectId) : true) && t.taakType !== 'FOCUS');
    const wacht = takenP.filter((t) => t.status === 'GEPLAND' && !t.terugmelding && t.taakType !== 'OVERLEG' && (t.einddatum ?? '') < vd);
    const nietKlaar = takenP.filter((t) => (t.terugmelding === 'NIET_KLAAR' || t.terugmelding === 'HERPLANNEN') && !t.tmAfgehandeld);
    const nietGepland = takenP.filter((t) => t.status !== 'GEPLAND');
    const wpLaat = d.werkpakketten
      .filter((w) => ids.has(w.projectId))
      .map((w) => ({ w, v: werkpakketVoortgang(w, d.werkpakketten.filter((x) => x.projectId === w.projectId), d.takenpakketten, d.taken, vd) }))
      .filter((x) => x.v.status === 'Te laat');
    const rijen = projecten
      .map((p) => {
        const budget = p.urenBudget ?? p.expertiseBudget.reduce((t, b) => t + b.uren, 0);
        const gepland = geplandeUren(d.taken.filter((t) => t.projectId === p.id)) + agileUren(d.takenpakketten.filter((t) => t.projectId === p.id)).besteed + (p.besteedLosMin ?? 0) / 60;
        const laat = !!(p.prognose && p.livegang && p.prognose > p.livegang);
        const status: 'laat' | 'op-tijd' | 'onbekend' = laat ? 'laat' : p.prognose && p.livegang ? 'op-tijd' : 'onbekend';
        return { p, budget, gepland, status, wpLaat: wpLaat.some((x) => x.w.projectId === p.id) };
      })
      .sort((a, b) => (a.status === 'laat' ? 0 : 1) - (b.status === 'laat' ? 0 : 1) || (a.p.livegang ?? '9').localeCompare(b.p.livegang ?? '9'));

    // komende 30 dagen
    const komend: { datum: string; titel: string; sub: string; icoon: IcoonNaam; naar: string }[] = [];
    for (const p of projecten) {
      if (p.livegang && p.livegang >= vd && p.livegang <= tot30) komend.push({ datum: p.livegang, titel: `Livegang ${p.naam}`, sub: `${p.nummer} · ${p.klant ?? ''}`, icoon: 'rocket', naar: `/projecten/${p.id}` });
      if (p.oplevering && p.oplevering >= vd && p.oplevering <= tot30) komend.push({ datum: p.oplevering, titel: `Oplevering ${p.naam}`, sub: `${p.nummer} · ${p.klant ?? ''}`, icoon: 'flag', naar: `/projecten/${p.id}` });
    }
    for (const w of d.werkpakketten) {
      if (!ids.has(w.projectId) || !w.deadline || w.deadline < vd || w.deadline > tot30) continue;
      const p = d.projecten.find((x) => x.id === w.projectId)!;
      komend.push({ datum: w.deadline, titel: `${w.naam} gereed`, sub: `${p.nummer} ${p.naam}`, icoon: 'circle-check', naar: `/projecten/${p.id}/werkpakketten` });
    }
    komend.sort((a, b) => a.datum.localeCompare(b.datum));

    // capaciteit en overvol (planner)
    const engs = d.engineers.filter((e) => e.actief !== false && e.inzet !== 'NIET_CAPACITEIT');
    const ma = maandagVan(vd);
    const dagen = [0, 1, 2, 3, 4, 7, 8, 9, 10, 11].map((n) => addDagen(ma, n));
    const capaciteit = dagen.map((dag) => {
      let vrij = 0;
      let ruimte = 0;
      let beschikbaar = 0;
      for (const e of engs) {
        const c = kal.capaciteit(e, dag);
        if (c <= 0) continue;
        beschikbaar += c;
        const v = Math.max(0, kal.vrij(e, dag));
        vrij += v;
        if (v >= 2) ruimte++;
      }
      return { dag, vrij, beschikbaar, ruimte };
    });
    const overvol = engs
      .map((e) => ({ e, dagen: dagen.slice(0, 5).filter((dag) => dag >= vd && kal.capaciteit(e, dag) > 0 && kal.vrij(e, dag) < -0.01) }))
      .filter((x) => x.dagen.length);
    const afwezig = d.afwezigheden.filter((a) => a.van <= vd && a.tot >= vd).map((a) => ({ a, naam: d.engineers.find((e) => e.id === a.engineerId)?.naam ?? '?' }));
    const syncFout = d.taken.filter((t) => t.sync === 'FOUT');
    return { vd, projecten, eigen: pmModus && eigen.length > 0, rijen, wacht, nietKlaar, nietGepland, wpLaat, komend, capaciteit, overvol, afwezig, syncFout, engs };
  }, [d, kal, s.email, s.naam, s.rol]);

  if (!d || !r) return <Laden />;
  const opTijd = r.rijen.filter((x) => x.status === 'op-tijd').length;
  const laat = r.rijen.filter((x) => x.status === 'laat').length;
  const onbekend = r.rijen.length - opTijd - laat;
  const actiePunten = [
    { n: r.wacht.length, titel: r.wacht.length === 1 ? 'taak wacht op terugmelding' : 'taken wachten op terugmelding', uitleg: 'De datum is voorbij, de engineer heeft nog niets gemeld.', icoon: 'clock' as IcoonNaam, naar: '/taken?status=Wacht%20op%20terugmelding', toon: 'oranje' },
    { n: r.nietKlaar.length, titel: r.nietKlaar.length === 1 ? 'taak niet klaar of herplannen' : 'taken niet klaar of herplannen', uitleg: 'Plan een vervolg of handel ze af.', icoon: 'refresh-cw' as IcoonNaam, naar: '/taken?status=Actie%20nodig', toon: 'rood' },
    { n: r.wpLaat.length, titel: r.wpLaat.length === 1 ? 'werkpakket te laat' : 'werkpakketten te laat', uitleg: 'Deadline voorbij en nog niet volledig ingepland.', icoon: 'triangle-alert' as IcoonNaam, naar: r.wpLaat[0] ? `/projecten/${r.wpLaat[0].w.projectId}/werkpakketten` : '/projecten', toon: 'rood' },
    ...(planner ? [{ n: r.nietGepland.length, titel: r.nietGepland.length === 1 ? 'taak nog niet ingepland' : 'taken nog niet ingepland', uitleg: 'Zonder engineer of datum.', icoon: 'list-checks' as IcoonNaam, naar: '/taken?status=Nog%20niet%20gepland', toon: 'blauw' }] : []),
    ...(planner ? [{ n: r.overvol.length, titel: r.overvol.length === 1 ? 'engineer overvol deze week' : 'engineers overvol deze week', uitleg: r.overvol.slice(0, 3).map((x) => `${x.e.naam.split(' ')[0]} (${x.dagen.map((dag) => DAGNAAM[verschilDagen(maandagVan(r.vd), dag)]).join(', ')})`).join(' · ') || 'Iedereen past.', icoon: 'users' as IcoonNaam, naar: '/weekrooster', toon: 'rood' }] : []),
  ];
  const maxCap = Math.max(1, ...r.capaciteit.map((c) => c.beschikbaar));
  const koppelingen = koppel.data;
  const kl = Array.isArray(koppelingen?.koppelingen) ? koppelingen!.koppelingen : [];
  const nietGekoppeld = koppelingen && !koppelingen.organisatie ? r.engs.filter((e) => !kl.some((k) => k.engineerId === e.id)).length : 0;
  const opnieuw = kl.filter((k) => k.status !== 'OK').length;
  const lic = s.licentie;

  return (
    <div className="pagina start">
      <div className="start-kop">
        <div className="groei">
          <h1>{groet()}, {s.naam.split(' ')[0]}</h1>
          <p className="mute">{fmtDag(r.vd, 'lang')} {r.vd.slice(0, 4)} · week {isoWeek(r.vd)}{s.rol === 'PROJECTMANAGER' ? (r.eigen ? ` · ${r.projecten.length} eigen projecten` : ' · alle actieve projecten') : ''}</p>
        </div>
        <div className="rij wrap" style={{ gap: 8 }}>
          {mag(s.rol, 'taken.aanmaken') && <Link className="knop" to="/taken/nieuw" state={taak.state}><Icoon naam="list-checks" maat={16} /> Nieuwe taak</Link>}
          {mag(s.rol, 'projecten.schrijven') && <Link className="knop" to="/projecten?nieuw=1"><Icoon naam="folder-kanban" maat={16} /> Nieuw project</Link>}
          {planner && <Link className="knop primair" to="/weekrooster"><Icoon naam="calendar-range" maat={16} /> Weekrooster</Link>}
        </div>
      </div>

      <div className="start-tegels">
        <Tegel label={s.rol === 'PROJECTMANAGER' && r.eigen ? 'Mijn projecten' : 'Actieve projecten'} getal={r.projecten.length} sub={`${opTijd} op tijd · ${laat} te laat · ${onbekend} zonder prognose`} icoon="folder-kanban" naar="/projecten" />
        <Tegel label="Prognose na livegang" getal={laat} sub={laat ? 'projecten lopen uit' : 'alles op schema'} icoon="triangle-alert" toon={laat ? 'rood' : 'groen'} naar="/projecten" />
        <Tegel label="Komende 30 dagen" getal={r.komend.filter((k) => k.icoon === 'rocket').length} sub={((l, dl) => `${l === 1 ? 'livegang' : 'livegangen'} · ${dl} ${dl === 1 ? 'deadline' : 'deadlines'}`)(r.komend.filter((k) => k.icoon === 'rocket').length, r.komend.filter((k) => k.icoon === 'circle-check').length)} icoon="rocket" naar="/tijdlijn" />
        <Tegel label="Actie nodig" getal={r.wacht.length + r.nietKlaar.length} sub="terugmeldingen open of niet klaar" icoon="clock" toon={r.wacht.length + r.nietKlaar.length ? 'oranje' : 'groen'} naar="/taken?status=Actie%20nodig" />
      </div>

      <div className="start-grid">
        <div className="kolom" style={{ gap: 16 }}>
          <section className="paneel">
            <div className="sectie-kop"><h2 className="groei">Actie nodig</h2></div>
            <div className="actie-lijst">
              {actiePunten.map((a) => (
                <Link key={a.titel} to={a.naar} className={`actie ${a.n ? `toon-${a.toon}` : 'rustig'}`}>
                  <span className="actie-icoon"><Icoon naam={a.icoon} maat={18} /></span>
                  <span className="groei">
                    <b>{a.n} {a.titel}</b>
                    <small>{a.uitleg}</small>
                  </span>
                  <Icoon naam="chevron-right" maat={16} className="mute" />
                </Link>
              ))}
            </div>
          </section>

          <section className="paneel">
            <div className="sectie-kop">
              <h2 className="groei">{s.rol === 'PROJECTMANAGER' && r.eigen ? 'Mijn projecten' : 'Projecten'}</h2>
              <Link to="/projecten" className="klein">Alle projecten →</Link>
            </div>
            <table className="tabel">
              <thead><tr><th>Project</th><th>Voortgang</th><th>Livegang</th><th>Status</th></tr></thead>
              <tbody>
                {r.rijen.slice(0, 8).map(({ p, budget, gepland, status, wpLaat }) => (
                  <tr key={p.id} className="klik" onClick={() => nav(`/projecten/${p.id}`)}>
                    <td><b>{p.nummer} {p.naam}</b><div className="klein mute">{p.klant} · {PROJECT_STATUS[p.status]}</div></td>
                    <td style={{ minWidth: 140 }}>
                      <div className="start-balk"><i style={{ width: `${budget ? Math.min(100, (gepland / budget) * 100) : 0}%` }} /></div>
                      <div className="klein mute">{fmtUren(gepland)} van {fmtUren(budget)} uur</div>
                    </td>
                    <td className="klein">{p.livegang ? fmtDag(p.livegang, 'jaar') : '–'}{p.prognose && <div className="mute">prognose {fmtDag(p.prognose, 'datum')}</div>}</td>
                    <td>
                      <span className={`start-status ${status}`}>{status === 'laat' ? 'Loopt uit' : status === 'op-tijd' ? 'Op tijd' : 'Geen prognose'}</span>
                      {wpLaat && <div className="klein" style={{ color: 'var(--rood)' }}>werkpakket te laat</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!r.rijen.length && <div className="leeg">Geen actieve projecten.</div>}
          </section>

          {planner && (
            <section className="paneel">
              <div className="sectie-kop"><h2 className="groei">Capaciteit van het team</h2><Link to="/bezetting" className="klein">Bezetting →</Link></div>
              <div className="cap-staven">
                {r.capaciteit.map((c, i) => (
                  <div key={c.dag} className={`cap-staaf ${c.dag === r.vd ? 'vandaag' : ''} ${i === 5 ? 'nieuwe-week' : ''}`} title={`${fmtDag(c.dag, 'lang')}: ${fmtUren(c.vrij)} van ${fmtUren(c.beschikbaar)} uur vrij`}>
                    <em>{fmtUren(c.vrij)}u</em>
                    <div className="cap-vak">
                      <i className="vrij" style={{ height: `${(c.vrij / maxCap) * 100}%` }} />
                      <i className="bezet" style={{ height: `${(Math.max(0, c.beschikbaar - c.vrij) / maxCap) * 100}%` }} />
                    </div>
                    <b>{DAGNAAM[i % 5]} {Number(c.dag.slice(8))}</b>
                    <span>{c.ruimte} met ruimte</span>
                  </div>
                ))}
              </div>
              <div className="rap-legenda"><span><i className="cap-l-vrij" />vrij</span><span><i className="cap-l-bezet" />gepland of bezet</span><span>deze week en volgende week · ruimte = minimaal 2 uur vrij</span></div>
            </section>
          )}
        </div>

        <div className="kolom" style={{ gap: 16 }}>
          <section className="paneel">
            <div className="sectie-kop"><h2 className="groei">Komende 30 dagen</h2></div>
            <div className="komend-lijst">
              {r.komend.slice(0, 8).map((k, i) => (
                <Link key={i} to={k.naar} className="komend">
                  <span className="komend-datum"><b>{Number(k.datum.slice(8))}</b><small>{fmtDag(k.datum, 'datum').split(' ')[1]}</small></span>
                  <span className="groei"><b><Icoon naam={k.icoon} maat={14} /> {k.titel}</b><small>{k.sub} · over {verschilDagen(r.vd, k.datum)} dagen</small></span>
                </Link>
              ))}
              {!r.komend.length && <div className="leeg">Geen livegangen of deadlines.</div>}
            </div>
          </section>

          {planner && (
            <section className="paneel">
              <div className="sectie-kop"><h2 className="groei">Afwezig vandaag</h2><Link to="/verlof" className="klein">Verlof →</Link></div>
              {r.afwezig.length ? (
                <ul className="start-lijstje">
                  {r.afwezig.map(({ a, naam }) => <li key={a.id}><b>{naam}</b><span className="mute"> · {a.soort.toLowerCase()}{a.vanTijd ? ` ${a.vanTijd}–${a.totTijd}` : ''} · t/m {fmtDag(a.tot, 'datum')}</span></li>)}
                </ul>
              ) : <div className="klein mute">Iedereen is er.</div>}
            </section>
          )}

          {s.rol === 'PROJECTMANAGER' && urenAan && mag(s.rol, 'uren.goedkeuren') && (
            <Link to="/uren/goedkeuren" className="paneel start-link"><Icoon naam="badge-check" /> <span className="groei"><b>Uren goedkeuren</b><small>Weekstaten van vorige week nakijken</small></span><Icoon naam="chevron-right" maat={16} /></Link>
          )}

          {beheerder && (
            <section className="paneel">
              <div className="sectie-kop"><h2 className="groei">Systeem</h2></div>
              <div className="actie-lijst">
                <Link to="/beheer/instellingen?tab=abonnement" className={`actie ${lic?.toegang === 'VOL' ? 'rustig' : 'toon-oranje'}`}>
                  <span className="actie-icoon"><Icoon naam="credit-card" maat={18} /></span>
                  <span className="groei"><b>{s.organisatie.abonnement === 'TRIAL' ? `Proefperiode tot ${fmtDag(s.organisatie.trialTot, 'datum')}` : s.organisatie.abonnement === 'STRIPE' ? 'Abonnement actief' : 'Contract'}</b><small>{lic?.melding ?? `Modules: ${lic?.modules.join(', ').toLowerCase() || 'basis'}`}</small></span>
                </Link>
                <Link to="/beheer/instellingen?tab=koppelingen" className={`actie ${r.syncFout.length ? 'toon-rood' : 'rustig'}`}>
                  <span className="actie-icoon"><Icoon naam="calendar-days" maat={18} /></span>
                  <span className="groei"><b>{r.syncFout.length} taken met een agendafout</b><small>Synchronisatie met Outlook of Google</small></span>
                </Link>
                <Link to="/beheer" className={`actie ${opnieuw + nietGekoppeld ? 'toon-oranje' : 'rustig'}`}>
                  <span className="actie-icoon"><Icoon naam="hard-hat" maat={18} /></span>
                  <span className="groei"><b>{opnieuw} opnieuw koppelen · {nietGekoppeld} niet gekoppeld</b><small>{koppelingen?.organisatie ? 'Organisatiekoppeling Microsoft 365 actief' : 'Agenda\'s van engineers'}</small></span>
                </Link>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function Tegel({ label, getal, sub, icoon, naar, toon }: { label: string; getal: number; sub: string; icoon: IcoonNaam; naar: string; toon?: 'rood' | 'groen' | 'oranje' }) {
  return (
    <Link to={naar} className={`start-tegel ${toon ? `toon-${toon}` : ''}`}>
      <span className="tegel-icoon"><Icoon naam={icoon} maat={20} /></span>
      <span className="tegel-label">{label}</span>
      <b>{getal}</b>
      <small>{sub}</small>
    </Link>
  );
}
